import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AnimatePresence } from 'framer-motion'
import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { filterCommandEntries, wrapIndex } from './helpers'
import {
  Backdrop,
  Panel,
  SearchWrapper,
  SearchGlyph,
  SearchInput,
  KeyHintRow,
  KeyHint,
  ResultsList,
  ResultRow,
  ResultGlyph,
  ResultBody,
  ResultLabel,
  ResultHint,
  EmptyState,
  FooterRow,
  FooterHint,
} from './styled'
import { CommandPaletteProps } from './types'

/**
 * Cmd+K command palette. Centered modal with a search input + filtered
 * results list. Keyboard-first: arrow keys to navigate, enter to run,
 * escape to close. Closes when a command runs.
 *
 * @param {CommandPaletteProps} props
 * @returns {JSX.Element | null}
 */
const CommandPalette = ({
  isOpen,
  onClose,
  entries,
}: CommandPaletteProps): JSX.Element | null => {
  const [query, setQuery] = useState<string>('')
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const inputReference = useRef<HTMLInputElement | null>(null)

  const filtered = useMemo(
    () => filterCommandEntries(entries, query),
    [entries, query],
  )

  useEffect(() => {
    if (isOpen === false) {
      setQuery('')
      setActiveIndex(0)
      return
    }

    const handle = setTimeout(() => {
      inputReference.current?.focus()
    }, 60)

    return () => {
      clearTimeout(handle)
    }
  }, [isOpen])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value)
  }

  const runEntry = (index: number): void => {
    const target = filtered[index]
    if (target === undefined) {
      return
    }

    target.onRun()
    onClose()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(previous => wrapIndex(previous + 1, filtered.length))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(previous => wrapIndex(previous - 1, filtered.length))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      runEntry(activeIndex)
    }
  }

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  if (isOpen === false) {
    return null
  }

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Panel
          initial={{ opacity: 0, y: -16, scale: 0.97 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.22 },
          }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          onClick={stopPropagation}
        >
          <SearchWrapper>
            <SearchGlyph aria-hidden="true">
              <Icon icon={faMagnifyingGlass} />
            </SearchGlyph>
            <SearchInput
              ref={inputReference}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search commands, pages, listings…"
              aria-label="Command palette search"
            />
            <KeyHintRow>
              <KeyHint>esc</KeyHint>
            </KeyHintRow>
          </SearchWrapper>

          {filtered.length === 0 ? (
            <EmptyState>
              No matches. Try a shorter query or open the menu.
            </EmptyState>
          ) : (
            <ResultsList>
              {filtered.map((entry, index) => (
                <ResultRow
                  key={entry.id}
                  isActive={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runEntry(index)}
                >
                  <ResultGlyph aria-hidden="true">
                    <Icon icon={entry.glyph} />
                  </ResultGlyph>
                  <ResultBody>
                    <ResultLabel>{entry.label}</ResultLabel>
                    <ResultHint>{entry.hint}</ResultHint>
                  </ResultBody>
                </ResultRow>
              ))}
            </ResultsList>
          )}

          <FooterRow>
            <FooterHint>
              <KeyHint>↑</KeyHint>
              <KeyHint>↓</KeyHint>
              navigate
            </FooterHint>
            <FooterHint>
              <KeyHint>↵</KeyHint>
              run command
            </FooterHint>
          </FooterRow>
        </Panel>
      </Backdrop>
    </AnimatePresence>
  )
}

export default CommandPalette
